// Hermes domain — TeamPlan: o VALUE OBJECT do modo equipe (sprint subagents-team).
//
// O plano vem de um LLM — INPUT NÃO-CONFIÁVEL por definição. Este módulo é a
// BOUNDARY [SEC] que o normaliza: parse tolerante (LLMs embrulham JSON em
// prosa/fences), validação com limites DUROS (clamp, dedupe, caps) e a regra
// de decisão solo-vs-equipe. Falha de parse/validação NUNCA vira erro — vira
// modo 'solo' (o chat segue o caminho normal; a equipe é um upgrade, jamais
// um bloqueio).
//
// PURO de verdade: zero imports, zero I/O, zero Date/Math.random. Tudo aqui é
// determinístico e testável sem mock — e o architecture gate do Hermes garante
// que continue assim.

export const TEAM_LIMITS = {
  reasoningMax: 2000,
  titleMax: 80,
  taskMax: 2000,
  roleMax: 40,
  capMin: 2,
  capMax: 8,
  capDefault: 4,
  childResultMax: 6000,
  totalResultsMax: 24000,
  briefingMax: 1200,
};

const SOLO = (reasoning = '') => ({ mode: 'solo', reasoning, subtasks: [] });

function clampStr(v, max) {
  return typeof v === 'string' ? v.trim().slice(0, max) : '';
}

// Extrai o primeiro objeto JSON balanceado de um texto (LLMs adoram prosa em
// volta). Varre por profundidade de chaves respeitando strings/escapes — não
// regex — então `{"a":"}"}` não engana o parser.
function extractBalancedJson(text) {
  const start = text.indexOf('{');
  if (start === -1) return null;
  let depth = 0;
  let inStr = false;
  let escaped = false;
  for (let i = start; i < text.length; i++) {
    const ch = text[i];
    if (inStr) {
      if (escaped) escaped = false;
      else if (ch === '\\') escaped = true;
      else if (ch === '"') inStr = false;
      continue;
    }
    if (ch === '"') inStr = true;
    else if (ch === '{') depth++;
    else if (ch === '}') {
      depth--;
      if (depth === 0) return text.slice(start, i + 1);
    }
  }
  return null;
}

/**
 * Parse tolerante do output do líder → objeto cru (ainda NÃO validado) ou null.
 * Tenta, nesta ordem: JSON puro → bloco ```fenced``` → primeiro {...} balanceado.
 */
export function parseTeamPlan(rawText) {
  if (typeof rawText !== 'string' || !rawText.trim()) return null;
  const text = rawText.trim();

  const candidates = [];
  candidates.push(text);
  const fence = text.match(/```(?:json)?\s*([\s\S]*?)```/i);
  if (fence?.[1]) candidates.push(fence[1].trim());
  const balanced = extractBalancedJson(text);
  if (balanced) candidates.push(balanced);

  for (const c of candidates) {
    try {
      const obj = JSON.parse(c);
      if (obj && typeof obj === 'object' && !Array.isArray(obj)) return obj;
    } catch {
      /* tenta o próximo candidato */
    }
  }
  return null;
}

/**
 * Valida/normaliza um plano cru → TeamPlan canônico (C1). É a boundary [SEC]:
 * - campos extras são IGNORADOS; tipos errados viram 'solo'
 * - strings clampadas nos limites; subtasks deduplicadas por task normalizada
 * - subtasks são clampadas em `cap` (1 sobrando ⇒ 'solo': equipe de 1 não é equipe)
 * Nunca lança.
 */
export function validateTeamPlan(obj, { cap = TEAM_LIMITS.capDefault } = {}) {
  if (!obj || typeof obj !== 'object' || Array.isArray(obj)) return SOLO();
  const reasoning = clampStr(obj.reasoning, TEAM_LIMITS.reasoningMax);
  if (obj.mode === 'solo') return SOLO(reasoning);
  if (obj.mode !== 'team') return SOLO(reasoning);

  const boundedCap = Math.max(
    TEAM_LIMITS.capMin,
    Math.min(TEAM_LIMITS.capMax, Number.isInteger(cap) ? cap : TEAM_LIMITS.capDefault),
  );

  const raw = Array.isArray(obj.subtasks) ? obj.subtasks : [];
  const seen = new Set();
  const subtasks = [];
  for (const item of raw) {
    if (subtasks.length >= boundedCap) break;
    if (!item || typeof item !== 'object') continue;
    const task = clampStr(item.task, TEAM_LIMITS.taskMax);
    if (!task) continue;
    const key = task.toLowerCase().replace(/\s+/g, ' ');
    if (seen.has(key)) continue;
    seen.add(key);
    subtasks.push({
      title: clampStr(item.title, TEAM_LIMITS.titleMax) || task.slice(0, TEAM_LIMITS.titleMax),
      task,
      role: clampStr(item.role, TEAM_LIMITS.roleMax) || 'operário',
    });
  }

  if (subtasks.length < TEAM_LIMITS.capMin) return SOLO(reasoning);
  return { mode: 'team', reasoning, subtasks };
}

/** Regra de decisão final (já normalizado): 'team' só com 2+ subtasks. */
export function decideMode(plan) {
  return plan?.mode === 'team' && Array.isArray(plan.subtasks) && plan.subtasks.length >= TEAM_LIMITS.capMin
    ? 'team'
    : 'solo';
}

const TRUNCATION_NOTE = '\n…[cortado para a síntese]';

/**
 * Corte determinístico dos resultados dos operários ANTES da síntese — o
 * análogo local do capText (LAW) para o prompt do sintetizador. Por-filho
 * primeiro, depois o total (corta do FIM da lista: os primeiros operários são
 * os mais prováveis de carregar o núcleo da tarefa).
 */
export function capChildResults(
  results,
  { maxCharsPerChild = TEAM_LIMITS.childResultMax, maxTotalChars = TEAM_LIMITS.totalResultsMax } = {},
) {
  const list = Array.isArray(results) ? results : [];
  let budget = maxTotalChars;
  return list.map((r) => {
    const full = typeof r === 'string' ? r : String(r ?? '');
    let text = full.slice(0, maxCharsPerChild);
    let truncated = text.length < full.length;
    if (text.length > budget) {
      text = text.slice(0, Math.max(0, budget));
      truncated = true;
    }
    budget -= text.length;
    return { text: truncated && text ? text + TRUNCATION_NOTE : text, truncated };
  });
}

/**
 * Briefing compartilhado do operário `index` — o bloco de "memória de equipe"
 * determinístico que cada filho recebe junto da própria subtask: objetivo
 * global + quem são os irmãos + o próprio papel. NÃO-LLM (barato, testável) e
 * capado em maxChars.
 */
export function buildBriefing({ objective, subtasks, index, maxChars = TEAM_LIMITS.briefingMax } = {}) {
  const list = Array.isArray(subtasks) ? subtasks : [];
  const me = list[index];
  if (!me) return '';
  const siblings = list
    .map((s, i) => `- A${i + 1} (${s.role}): ${s.title}${i === index ? ' ← VOCÊ' : ''}`)
    .join('\n');
  const text = [
    `[Briefing da equipe — você é o operário A${index + 1}]`,
    `Objetivo global: ${clampStr(objective, TEAM_LIMITS.taskMax)}`,
    `Equipe:`,
    siblings,
    `Faça SÓ a sua parte (A${index + 1}). Não repita o trabalho dos irmãos. Seja direto no resultado final.`,
  ].join('\n');
  return text.slice(0, maxChars);
}
